import { create } from 'zustand'

const defaultTableState = () => ({
    selectionMode: 'include', // 'include' | 'exclude'
    selectedIds: new Set(),
    deselectedIds: new Set(),
})

const useTableSelectionStore = create((set, get) => ({
    tables: {},

    _getTable: (tableId) => get().tables[tableId] || defaultTableState(),

    toggleItem: (tableId, id) => set((state) => {
        const table = state.tables[tableId] || defaultTableState();
        if (table.selectionMode === 'include') {
            const next = new Set(table.selectedIds);
            if (next.has(id)) next.delete(id); else next.add(id);
            return { tables: { ...state.tables, [tableId]: { ...table, selectedIds: next } } };
        } else {
            const next = new Set(table.deselectedIds);
            if (next.has(id)) next.delete(id); else next.add(id);
            return { tables: { ...state.tables, [tableId]: { ...table, deselectedIds: next } } };
        }
    }),

    selectAll: (tableId) => set((state) => ({
        tables: {
            ...state.tables,
            [tableId]: { selectionMode: 'exclude', selectedIds: new Set(), deselectedIds: new Set() }
        }
    })),

    clearSelection: (tableId) => set((state) => {
        // Delete the entry — only selection state lives under tables[tableId]
        // and every reader falls back to defaultTableState(), so a reset
        // entry would just be a retained zombie key.
        if (!state.tables[tableId]) return state;
        const tables = { ...state.tables };
        delete tables[tableId];
        return { tables };
    }),

    handleSelectAll: (tableId) => {
        const table = get().tables[tableId] || defaultTableState();
        if (table.selectionMode === 'exclude' && table.deselectedIds.size === 0) {
            get().clearSelection(tableId);
        } else {
            get().selectAll(tableId);
        }
    },

    isSelected: (tableId, id) => {
        const table = get().tables[tableId] || defaultTableState();
        if (table.selectionMode === 'include') return table.selectedIds.has(id);
        return !table.deselectedIds.has(id);
    },

    getSelectionCount: (tableId, totalCount) => {
        const table = get().tables[tableId] || defaultTableState();
        if (table.selectionMode === 'include') return table.selectedIds.size;
        return (totalCount || 0) - table.deselectedIds.size;
    },

    getSelectionState: (tableId) => {
        return get().tables[tableId] || defaultTableState();
    },
}))

export default useTableSelectionStore
